import { path } from 'ramda'
import { Query } from 'mingo'

function ValidateCondition(condition, component = '', data) {
  const isDynamic = isDynamicComponent(component)

  if (!condition) return true
  if (!isDynamic) return new Query(condition).test(data)

  if ('*' in condition) return new Query(condition['*']).test(data)

  // Less conditions than components -> let each field do the check seperatly
  if (component.split(',').length > Object.keys(condition).length) return true

  // If all conditions fail -> hide
  return Object.values(condition).some(c => new Query(c).test(data))
}

function Filter(value, options = {}) {
  let { keys = [], placement = [] } = options
  let pattern = new RegExp('^/(.+)/$', 'gi')

  if (!(keys instanceof Array)) keys = [keys]
  if (!(placement instanceof Array)) placement = [placement]

  const Filters = {
    keys: field => {
      if (!keys.length) return true
      return keys.includes(field.key)
    },
    placement: field => {
      if (!placement.length) return true
      return placement.some(key => {
        if (!field.placement) return
        if (key && key.match(pattern)) return field.placement.match(pattern.exec(key)[1])
        return key === field.placement
      })
    },
  }

  return value.filter(field => Object.values(Filters).every(f => f(field)))
}

const Clone = function () {
  return JSON.parse(JSON.stringify.apply(this, arguments))
}

function GetCustomFieldsRules(value, filter) {
  if (filter) value = Filter(value, filter)
  let fieldsWithValidation = value.filter(c => path(['options', 'validation'], c))
  return fieldsWithValidation.reduce((res, cur) => {
    res[`customFields.${cur.key}`] = cur.options.validation
    return res
  }, {})
}

function ValueComp() {
  return {
    get() {
      return this.value
    },
    set(val) {
      this.$emit('input', val)
    },
  }
}

function ExtractString(str, pattern, options = {}) {
  let { group = 1 } = options
  let match = str.match(pattern)
  if (!match) return

  return match[group]
}

const BracketsRegex = /^\[.+\](:(\d+(fr|px|%)))?$/i
function RemoveBrackets(val) {
  if (!val.match(BracketsRegex)) return val
  return val.replace(/^\[/g, '').replace(/\](:\d+(fr|px|%))?$/g, '')
}

/*
  Hentet fra
  https://gist.github.com/softwarespot/76252a838efdcace2df1f9c724e37351
*/
function Evaluate(code, args = {}) {
  return function evaluateEval() {
    const argsStr = Object.keys(args)
      .map(key => `${key} = this.${key}`)
      .join(',')
    const argsDef = argsStr ? `let ${argsStr};` : ''

    return eval(`${argsDef}${code}`)
  }.call(args)
}

function Debounce(func, wait, immediate) {
  let timeout
  return function () {
    let context = this,
      args = arguments
    let later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

function CopyToClipboard(str) {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

function isDynamicComponent(value) {
  return Boolean(value.match(/.+:.+:.+/))
}

function DynamicComponentToString(value) {
  const wrapList = (v, isList) => (isList ? `[${v}]` : v)
  const addItemMinWidth = (v, w) => (w ? `${v}:${w}` : v)
  const createEntry = ({ component, key, label, isList, itemMinWidth }) => {
    return addItemMinWidth(wrapList(`${component}:${key}:${label}`, isList), itemMinWidth)
  }

  return addItemMinWidth(
    wrapList(value.components.map(createEntry).join(','), value.isList),
    value.itemMinWidth,
  )
}
function DynamicComponentToObject(value) {
  const components = RemoveBrackets(value)
    .split(',')
    .map(entry => {
      const [component, key, label] = RemoveBrackets(entry).split(':')
      return {
        component,
        key,
        label,
        isList: Boolean(entry.match(BracketsRegex)),
        itemMinWidth: ExtractString(entry, BracketsRegex, { group: 2 }),
      }
    })

  return {
    components,
    isList: Boolean(value.match(BracketsRegex)),
    itemMinWidth: ExtractString(value, BracketsRegex, { group: 2 }),
  }
}

export {
  CopyToClipboard,
  Filter,
  ValueComp,
  Clone,
  GetCustomFieldsRules,
  RemoveBrackets,
  BracketsRegex,
  ExtractString,
  Evaluate,
  Debounce,
  DynamicComponentToString,
  DynamicComponentToObject,
  ValidateCondition,
}
